import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { getCurrentHospitalId } from '@/lib/hospitalContext';

export interface User {
  id: string;
  name: string;
  email: string;
  role: "super_admin" | "admin" | "doctor" | "staff";
  status: "verified" | "pending" | "unverified"; // Email verification status
  specialty?: string;
  registrationNumber?: string;
  avatarUrl?: string;
}

interface UsersState {
  users: User[];
  isLoading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  addUser: (user: Omit<User, 'id'>) => Promise<void>;
  updateUser: (user: User) => Promise<void>;
  removeUser: (id: string) => Promise<void>;
  removeUserWithCascade: (id: string) => Promise<void>;
}

export const useUsersStore = create<UsersState>()((set, get) => ({
  users: [],
  isLoading: false,
  error: null,

  fetchUsers: async () => {
    set({ isLoading: true, error: null });
    try {
      // Get current hospital ID for multi-tenant filtering
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        console.warn('No hospital ID found, cannot fetch users');
        set({ users: [], isLoading: false, error: 'No hospital context found' });
        return;
      }

      console.log('Fetching users for hospital:', hospitalId);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('hospital_id', hospitalId) // Filter by current hospital
        .order('name');

      if (error) {
        console.error('Error fetching users:', error);
        throw error;
      }
      
      console.log(`Fetched ${data?.length || 0} users from database`);
      console.log('Users data:', data);
      
      // Map DB (snake_case) → app model (camelCase)
      // Handle status conversion: old values (active/inactive) or new values (verified/pending/unverified)
      const mapped = (data || []).map((row: any) => {
        // Convert old status values to new email verification status
        let status: "verified" | "pending" | "unverified" = 'pending';
        
        if (row.status) {
          const statusLower = String(row.status).toLowerCase();
          if (statusLower === 'verified' || statusLower === 'active') {
            status = 'verified';
          } else if (statusLower === 'unverified' || statusLower === 'inactive') {
            status = 'unverified';
          } else if (statusLower === 'pending') {
            status = 'pending';
          } else {
            // Unknown status, default based on common pattern
            status = statusLower.includes('verified') || statusLower.includes('active') ? 'verified' : 'pending';
          }
        }
        
        return {
          id: row.id,
          name: row.name,
          email: row.email,
          role: row.role,
          status,
          specialty: row.specialty ?? undefined,
          registrationNumber: row.registration_number ?? undefined,
          avatarUrl: row.avatar_url ?? undefined,
        };
      });
      
      console.log(`Mapped ${mapped.length} users`);
      set({ users: mapped, isLoading: false });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  addUser: async (user) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('profiles')
        .insert([user]);

      if (error) throw error;
      
      // Since we're not getting the inserted data back, we'll need to refetch
      await get().fetchUsers();
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  updateUser: async (updatedUser) => {
    set({ isLoading: true, error: null });
    try {
      // Get current hospital ID for multi-tenant filtering
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        throw new Error('No hospital context found');
      }

      // Map app model (camelCase) → DB (snake_case)
      const payload: any = {
        name: updatedUser.name,
        email: updatedUser.email,
        role: updatedUser.role,
        status: updatedUser.status,
        specialty: updatedUser.specialty ?? null,
        registration_number: updatedUser.registrationNumber ?? null,
      };
      const { error } = await supabase
        .from('profiles')
        .update(payload)
        .eq('id', updatedUser.id)
        .eq('hospital_id', hospitalId); // Ensure multi-tenant isolation

      if (error) throw error;
      set((state) => ({
        users: state.users.map((user) =>
          user.id === updatedUser.id ? updatedUser : user
        ),
        isLoading: false
      }));
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  removeUser: async (id) => {
    set({ isLoading: true, error: null });
    try {
      // Get current hospital ID for multi-tenant filtering
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        throw new Error('No hospital context found');
      }

      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', id)
        .eq('hospital_id', hospitalId); // Ensure multi-tenant isolation

      if (error) throw error;
      set((state) => ({
        users: state.users.filter((user) => user.id !== id),
        isLoading: false
      }));
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  removeUserWithCascade: async (id) => {
    set({ isLoading: true, error: null });
    try {
      // Get current hospital ID for multi-tenant filtering
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        throw new Error('No hospital context found');
      }

      console.log('Starting cascade deletion for user:', id);
      
      // First, delete from doctors table if the user is a doctor
      const userToDelete = get().users.find(user => user.id === id);
      if (userToDelete?.role === 'doctor') {
        console.log('User is a doctor, deleting from doctors table');
        const { error: doctorError } = await supabase
          .from('doctors')
          .delete()
          .eq('id', id)
          .eq('hospital_id', hospitalId); // Ensure multi-tenant isolation
        
        if (doctorError) {
          console.error('Error deleting from doctors table:', doctorError);
          throw new Error(`Failed to delete doctor record: ${doctorError.message}`);
        }
        console.log('Successfully deleted from doctors table');
      }

      // Note: This function marks user as unverified instead of inactive
      // For complete deletion, use removeUserCompletely instead
      console.log('Marking user email as unverified in profiles table');
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ status: 'unverified' })
        .eq('id', id)
        .eq('hospital_id', hospitalId); // Ensure multi-tenant isolation

      if (profileError) {
        console.error('Error updating profile status:', profileError);
        throw new Error(`Failed to disable user profile: ${profileError.message}`);
      }

      console.log('Successfully marked user email as unverified');

      // Note: This function is for deactivation (marks email as unverified)
      // For complete deletion, use removeUserCompletely instead
      // The user cannot login if email is unverified
      console.log('User email marked as unverified and deleted from doctors table - login effectively disabled');
      
      // Update local state
      set((state) => ({
        users: state.users.filter((user) => user.id !== id),
        isLoading: false
      }));
      
      console.log('Cascade deletion completed successfully');
    } catch (error) {
      console.error('Cascade deletion error:', error);
      set({ error: (error as Error).message, isLoading: false });
      throw error; // Re-throw to allow calling code to handle
    }
  },
})); 